import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { X, ChevronsUpDown, AlertTriangle, KeyRound, CheckCircle2, Sparkles } from 'lucide-react';
import FileUpload from '@/components/FileUpload';
import VoiceRecognition from '@/components/VoiceRecognition';
import { wrapTextareaSelection } from '@/lib/formattedText';
import FreeTextNotice from '@/components/FreeTextNotice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Retourne la date/heure actuelle (+ decalage optionnel en minutes) au format
// datetime-local, en heure de Paris (et non UTC)
export const getParisDateTimeLocal = (offsetMinutes = 0) => {
  const now = new Date(Date.now() + offsetMinutes * 60000);
  const parts = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(now).reduce((acc, part) => {
    acc[part.type] = part.value;
    return acc;
  }, {});
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

export const CATEGORIES = [
  { value: 'Général', types: ['Prise de service', 'Fin de service', 'Autre'] },
  { value: 'Sûreté', types: ['Ronde', 'Escorte', 'Intervention', 'Formation', 'Alarme', 'Appréhension', 'Objet trouvé', 'Litige/Conflit', 'Prélèvement', 'Expulsion', 'Autre'] },
  { value: 'Incendie', types: ['Ronde', 'Intervention', 'Alarme', 'IFEA', 'Formation', 'Exercice', 'Levée de doute', 'Essai', 'Autre'] },
  { value: 'Secours à personnes', types: ['Client', 'Employé', 'Formation', 'Exercice', 'Autre'] },
  { value: 'Technique', types: ['Maintenance préventive', 'Réparation d\'urgence', 'Vérification des systèmes', 'Panne matériel', 'Coupure électrique', 'Autre'] },
  { value: 'Divers', types: ['Départ clé', 'Retour clé', 'Départ badge', 'Retour badge', 'Livraison', 'Visite', 'Autre'] }
];

// Sous-types de l'alarme : Incendie propose en plus "Déclenchement",
// propre à ce contexte (non pertinent pour Sûreté).
export const getAlarmSubtypes = () =>
  ['Armement', 'Désarmement', 'Essai', 'Déclenchement', 'Défaut', 'Mise hors service', 'Mise en service', 'Maintenance'];

// Sous-types de "Secours à personnes" (Client/Employé) : précise s'il
// s'agit d'un malaise ou d'une blessure (ex: "Client" + "Blessure").
export const SECOURS_SUBTYPES = ['Malaise', 'Blessure'];

// Matériel à cocher lors de la prise/fin de service. Radio et téléphone
// demandent en plus l'état (bon état/abîmé) ; "Autres" n'a qu'un commentaire
// libre pour préciser de quoi il s'agit, sans état à qualifier.
const MATERIEL_ITEMS = [
  { key: 'radio', label: 'Radio', hasEtat: true, hasComment: true },
  { key: 'controleur', label: 'Contrôleur', hasEtat: false, hasComment: false },
  { key: 'cles', label: 'Clés', hasEtat: false, hasComment: false },
  { key: 'telephone', label: 'Téléphone', hasEtat: true, hasComment: true },
  { key: 'badge', label: 'Badge', hasEtat: false, hasComment: false },
  { key: 'autres', label: 'Autres', hasEtat: false, hasComment: true },
];

// Liste à plat de toutes les combinaisons catégorie/type/sous-type, pour la
// recherche directe d'un type d'événement (ex: taper "prise de service" ou
// "alarme armement" et tout remplir automatiquement en un seul choix -
// catégorie, type, et sous-type quand il y en a un).
const ALL_EVENT_TYPES = CATEGORIES.flatMap((cat) => cat.types.flatMap((type) => {
  const isAlarmType = type === 'Alarme';
  const isSecoursType = cat.value === 'Secours à personnes' && ['Client', 'Employé'].includes(type);

  if (isAlarmType) {
    return getAlarmSubtypes(cat.value).map((subtype) => ({
      category: cat.value, type, subtypeField: 'alarm_subtype', subtype, label: `${type} - ${subtype}`
    }));
  }
  if (isSecoursType) {
    return SECOURS_SUBTYPES.map((subtype) => ({
      category: cat.value, type, subtypeField: 'secours_subtype', subtype, label: `${type} - ${subtype}`
    }));
  }
  return [{ category: cat.value, type, label: type }];
}));

// Construit une description lisible à partir du matériel coché, pour que le
// champ "description" (obligatoire côté serveur) reste renseigné même si la
// saisie libre est masquée pour la prise/fin de service.
const buildMaterielDescription = (materiel, verbe) => {
  const items = MATERIEL_ITEMS.filter(item => materiel?.[item.key]?.checked);
  if (items.length === 0) {
    return `Aucun matériel ${verbe}.`;
  }
  const parts = items.map(item => {
    const data = materiel[item.key];
    const etatLabel = item.hasEtat ? (data.etat === 'abime' ? 'Abîmé' : 'Bon état') : '';
    const comment = item.hasComment && data.commentaire?.trim() ? data.commentaire.trim() : '';
    const details = [etatLabel, comment].filter(Boolean).join(' — ');
    return details ? `${item.label} (${details})` : item.label;
  });
  return `Matériel ${verbe} : ${parts.join(', ')}.`;
};

const EventForm = ({ sites, user, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState(() => ({
    category: '',
    event_type: '',
    // Présélectionné dès le montage si un seul site est déjà accessible à ce moment-là
    site_id: (sites && sites.length === 1) ? sites[0].id : '',
    description: '',
    event_datetime: getParisDateTimeLocal(),
    extra_fields: {},
    attachments: [],
    is_important: false
  }));
  const [signature, setSignature] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef(null);
  const descriptionTextareaRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [showNoMaterielConfirm, setShowNoMaterielConfirm] = useState(false);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const isSiteAccount = user?.role === 'site';
  const canMarkImportant = user?.role === 'admin' || user?.role === 'chef_poste';
  const [siteAgents, setSiteAgents] = useState([]);
  const [redacteurId, setRedacteurId] = useState('');
  const [redacteurPickerOpen, setRedacteurPickerOpen] = useState(false);
  // Code personnel à 4 chiffres du rédacteur, exigé uniquement sur un compte
  // de site partagé, pour confirmer que c'est bien lui qui saisit l'événement.
  // Demandé via une popup dédiée : dès les 4 chiffres saisis, le code est
  // vérifié immédiatement auprès du serveur (et non plus seulement à la
  // soumission finale du formulaire).
  const [redacteurPin, setRedacteurPin] = useState('');
  const [redacteurPinDialogOpen, setRedacteurPinDialogOpen] = useState(false);
  const [pinChecking, setPinChecking] = useState(false);
  const [pinVerified, setPinVerified] = useState(false);
  const [pinError, setPinError] = useState('');
  // Clés actuellement perçues et non rendues sur le site sélectionné, pour
  // proposer une liste à cocher lors d'un retour de clé plutôt qu'une saisie
  // manuelle du numéro (voir GET /api/keys/pending-status).
  const [pendingKeysForReturn, setPendingKeysForReturn] = useState([]);
  const [selectedReturnKeyNumbers, setSelectedReturnKeyNumbers] = useState([]);

  // Quand un seul site est accessible (compte affecté à un seul site, ou
  // session verrouillée sur un site précis), pas besoin de le faire choisir
  // à chaque événement : il est présélectionné automatiquement.
  useEffect(() => {
    if (sites.length === 1 && formData.site_id !== sites[0].id) {
      setFormData(prev => ({ ...prev, site_id: sites[0].id }));
    }
    // eslint-disable-next-line
  }, [sites]);

  // Charge la liste des agents affectés au site dès qu'un site est sélectionné,
  // uniquement utile pour un compte "Site" qui doit désigner un agent rédacteur.
  useEffect(() => {
    if (!isSiteAccount || !formData.site_id) { setSiteAgents([]); return; }
    const token = localStorage.getItem('token');
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/sites/${formData.site_id}/agents`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setSiteAgents(await res.json());
      } catch { /* silencieux */ }
    })();
    setRedacteurId('');
    setRedacteurPin('');
    setPinVerified(false);
    setPinError('');
  }, [isSiteAccount, formData.site_id]);

  // Charge la liste des clés perçues et non encore rendues sur le site
  // sélectionné, dès qu'on choisit "Retour clé", pour proposer une liste à
  // cocher plutôt qu'une saisie manuelle du numéro.
  useEffect(() => {
    setSelectedReturnKeyNumbers([]);
    if (formData.event_type !== 'Retour clé' || !formData.site_id) {
      setPendingKeysForReturn([]);
      return;
    }
    const token = localStorage.getItem('token');
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/keys/pending-status?site_id=${formData.site_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setPendingKeysForReturn(data.keys || []);
        }
      } catch { /* silencieux */ }
    })();
  }, [formData.event_type, formData.site_id]);

  const toggleReturnKey = (badgeNumber, checked) => {
    setSelectedReturnKeyNumbers((prev) => checked ? [...prev, badgeNumber] : prev.filter((n) => n !== badgeNumber));
  };

  // Suggère automatiquement le prochain numéro d'appréhension du site
  // (numérotation propre à chaque site, voir GET /events/next-apprehension-number),
  // pour éviter d'avoir à le rechercher à chaque fois. Ne l'écrase jamais
  // s'il a déjà une valeur (saisie manuelle, ou déjà chargée).
  useEffect(() => {
    if (formData.event_type !== 'Appréhension' || !formData.site_id) return;
    if (formData.extra_fields.apprehension_number) return;
    const token = localStorage.getItem('token');
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/events/next-apprehension-number?site_id=${formData.site_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setFormData((prev) => (prev.extra_fields.apprehension_number ? prev : {
            ...prev,
            extra_fields: { ...prev.extra_fields, apprehension_number: String(data.next_number) }
          }));
        }
      } catch { /* silencieux */ }
    })();
    // eslint-disable-next-line
  }, [formData.event_type, formData.site_id]);

  // Vérifie immédiatement le code auprès du serveur dès que les 4 chiffres
  // sont saisis, pour un retour instantané plutôt que d'attendre la
  // soumission complète du formulaire.
  const verifyRedacteurPin = async (pin, agentId) => {
    setPinChecking(true);
    setPinError('');
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/events/verify-redacteur-pin`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ redacteur_id: agentId, pin })
      });
      if (res.ok) {
        setPinVerified(true);
        setRedacteurPinDialogOpen(false);
      } else {
        const err = await res.json();
        setPinError(err.detail || 'Code incorrect');
        setPinVerified(false);
        setRedacteurPin('');
      }
    } catch {
      setPinError('Erreur de connexion, merci de réessayer');
      setPinVerified(false);
    } finally {
      setPinChecking(false);
    }
  };

  const selectedCategory = CATEGORIES.find(c => c.value === formData.category);
  const needsSignature = formData.event_type === 'Prise de service' || formData.event_type === 'Fin de service';
  const isApprehension = formData.event_type === 'Appréhension';
  const isBadgeOrKey = ['Départ badge', 'Retour badge', 'Départ clé', 'Retour clé'].includes(formData.event_type);
  const isKeyDeparture = formData.event_type === 'Départ clé';
  const isAlarm = ['Sûreté', 'Incendie'].includes(formData.category) && formData.event_type === 'Alarme';
  const isSecours = formData.category === 'Secours à personnes' && ['Client', 'Employé'].includes(formData.event_type);
  const isPriseService = formData.event_type === 'Prise de service';
  const isFinService = formData.event_type === 'Fin de service';
  const isServiceCheckin = isPriseService || isFinService;
  const materiel = formData.extra_fields.materiel || {};
  const noMaterielChecked = isServiceCheckin && MATERIEL_ITEMS.every(item => !materiel[item.key]?.checked);

  // Un agent (compte personnel) qui date un événement de plus de 15 minutes
  // dans le passé n'est jamais bloqué : l'événement est créé tout de suite et
  // attendra la validation d'un chef de poste depuis son propre compte (voir
  // create_event côté serveur) - purement informatif ici.
  const isLateEntry = user?.role === 'agent' && !!formData.event_datetime
    && new Date(formData.event_datetime) < new Date(Date.now() - 15 * 60000);

  const updateMateriel = (key, patch) => {
    setFormData(prev => ({
      ...prev,
      extra_fields: {
        ...prev.extra_fields,
        materiel: {
          ...prev.extra_fields.materiel,
          [key]: { ...(prev.extra_fields.materiel?.[key] || {}), ...patch }
        }
      }
    }));
  };

  // Le canvas a une résolution interne fixe (500x200) mais s'affiche en
  // largeur variable (w-full) : sans cette mise à l'échelle, le trait dessiné
  // se décale par rapport au curseur/doigt dès que la taille affichée diffère
  // de la résolution interne (notamment sur un grand écran d'ordinateur).
  const getCanvasPoint = (canvas, e) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();

    const { x, y } = getCanvasPoint(canvas, e);

    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const { x, y } = getCanvasPoint(canvas, e);

    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    if (canvasRef.current) {
      setSignature(canvasRef.current.toDataURL());
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignature(null);
    }
  };

  const handleCorrectText = async () => {
    if (!formData.description.trim()) {
      toast.error('Rédigez du texte avant de le corriger');
      return;
    }
    setCorrecting(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/events/correct-text`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: formData.description })
      });
      if (res.ok) {
        const data = await res.json();
        setFormData(prev => ({ ...prev, description: data.corrected_text }));
        toast.success('Texte corrigé');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de la correction');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setCorrecting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.category) {
      toast.error('La catégorie est obligatoire');
      return;
    }

    if (!formData.event_type) {
      toast.error('Le type d\'événement est obligatoire');
      return;
    }

    if (user?.role !== 'admin' && formData.event_datetime && new Date(formData.event_datetime) > new Date(Date.now() + 5 * 60000)) {
      toast.error('Impossible de créer un événement daté de plus de 5 minutes dans le futur');
      return;
    }

    if (needsSignature && !signature) {
      toast.error('La signature est requise pour ce type d\'événement');
      return;
    }

    if (isAlarm && !formData.extra_fields.alarm_subtype) {
      toast.error('Le sous-type de l\'alarme est requis');
      return;
    }

    if (isSecours && !formData.extra_fields.secours_subtype) {
      toast.error('Le sous-type (Malaise/Blessure) est requis');
      return;
    }

    if (isKeyDeparture) {
      const requiredKeyFields = [
        ['company_name', 'le nom de la société'],
        ['technician_name', 'le nom du technicien'],
        ['phone_number', 'le numéro de téléphone'],
        ['badge_number', 'le numéro de clé'],
      ];
      if (!formData.extra_fields.departure_final) {
        requiredKeyFields.push(
          ['alert_time_1', 'le 1ᵉʳ rappel de non-restitution'],
          ['alert_time_2', 'le 2ᵉ rappel de non-restitution'],
        );
      }
      const missingKeyFields = requiredKeyFields.filter(([field]) => !String(formData.extra_fields[field] || '').trim());
      if (missingKeyFields.length > 0) {
        toast.error(`Champs obligatoires manquants : ${missingKeyFields.map(([, label]) => label).join(', ')}`);
        return;
      }
    }

    if (formData.event_type === 'Retour clé') {
      if (pendingKeysForReturn.length > 0) {
        if (selectedReturnKeyNumbers.length === 0) {
          toast.error('Sélectionnez au moins une clé restituée');
          return;
        }
      } else if (!String(formData.extra_fields.badge_number || '').trim()) {
        toast.error('Le numéro de clé est obligatoire pour un retour de clé');
        return;
      }
    }

    if (isServiceCheckin) {
      const missingEtat = MATERIEL_ITEMS.some(item => item.hasEtat && materiel[item.key]?.checked && !materiel[item.key]?.etat);
      if (missingEtat) {
        toast.error('Précisez l\'état (bon état/abîmé) pour chaque matériel coché concerné');
        return;
      }
    }

    if (isSiteAccount && !redacteurId) {
      toast.error('L\'agent rédacteur est obligatoire pour ce compte');
      return;
    }

    if (isSiteAccount && !pinVerified) {
      toast.error('Le code personnel de l\'agent rédacteur doit être saisi et confirmé');
      setRedacteurPinDialogOpen(true);
      return;
    }

    // Aucun matériel coché : on ne bloque pas, mais on demande confirmation
    // via une popup au moment de créer l'événement, plutôt qu'un avertissement
    // affiché en continu pendant la saisie.
    if (noMaterielChecked) {
      setShowNoMaterielConfirm(true);
      return;
    }

    await submitEvent();
  };

  const submitEvent = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      // Retour de clé via la liste à cocher : un événement par clé
      // sélectionnée, avec une description générée automatiquement.
      if (formData.event_type === 'Retour clé' && selectedReturnKeyNumbers.length > 0) {
        for (const badgeNumber of selectedReturnKeyNumbers) {
          const key = pendingKeysForReturn.find((k) => k.extra_fields?.badge_number === badgeNumber);
          const payload = {
            ...formData,
            extra_fields: { ...formData.extra_fields, badge_number: badgeNumber },
            description: `Restitution de la clé N° ${badgeNumber} par la société ${key?.extra_fields?.company_name || ''} (${key?.extra_fields?.technician_name || ''})`,
            signature_data: signature,
            ...(isSiteAccount ? { redacteur_id: redacteurId, redacteur_pin: redacteurPin } : {})
          };
          const response = await fetch(`${BACKEND_URL}/api/events`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          if (!response.ok) {
            const error = await response.json();
            toast.error(error.detail || 'Erreur lors de la création');
            if (isSiteAccount) {
              setRedacteurPin('');
              setPinVerified(false);
            }
            return;
          }
        }
        onSuccess();
        return;
      }

      const payload = {
        ...formData,
        ...(isServiceCheckin ? { description: buildMaterielDescription(materiel, isPriseService ? 'perçu' : 'rendu') } : {}),
        signature_data: signature,
        ...(isSiteAccount ? { redacteur_id: redacteurId, redacteur_pin: redacteurPin } : {})
      };

      const response = await fetch(`${BACKEND_URL}/api/events`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création');
        // En cas d'échec sur un compte de site (souvent un code personnel
        // incorrect), on efface le code pour forcer une nouvelle saisie.
        if (isSiteAccount) {
          setRedacteurPin('');
          setPinVerified(false);
        }
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Champ description, affiché en position normale pour tous les types
  // d'événement sauf "Départ clé" où il est déplacé après le nom de la
  // société/technicien/téléphone/numéro de clé (voir plus bas).
  const descriptionField = (
    <div className="space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Label htmlFor="description">Description *</Label>
        <div className="flex gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0 font-bold"
            onClick={() => wrapTextareaSelection(descriptionTextareaRef, formData.description, (v) => setFormData((prev) => ({ ...prev, description: v })), '**')}
            title="Gras"
            data-testid="event-description-bold-btn"
          >
            G
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0 underline"
            onClick={() => wrapTextareaSelection(descriptionTextareaRef, formData.description, (v) => setFormData((prev) => ({ ...prev, description: v })), '__')}
            title="Souligné"
            data-testid="event-description-underline-btn"
          >
            S
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0 italic"
            onClick={() => wrapTextareaSelection(descriptionTextareaRef, formData.description, (v) => setFormData((prev) => ({ ...prev, description: v })), '*')}
            title="Italique"
            data-testid="event-description-italic-btn"
          >
            I
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCorrectText}
            disabled={correcting}
            data-testid="correct-event-text-btn"
          >
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />
            {correcting ? 'Correction en cours...' : 'Corriger mes fautes'}
          </Button>
        </div>
      </div>
      <VoiceRecognition
        onTranscript={(chunk) => setFormData(prev => ({ ...prev, description: prev.description + chunk }))}
        fieldName="description"
      />
      <Textarea
        ref={descriptionTextareaRef}
        id="description"
        data-testid="event-description"
        placeholder="Décrivez l'événement ou utilisez la dictée vocale..."
        value={formData.description}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        rows={4}
        required
      />
      <FreeTextNotice withSpellcheck />
      <p className="text-xs text-gray-500 dark:text-slate-400">
        Vous pouvez dicter votre rapport ou le saisir manuellement. Sélectionnez du texte puis G (gras), S (souligné) ou I (italique) pour le mettre en forme. La correction est possible après dictée.
      </p>
    </div>
  );

  return (
    <>
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="event-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="site">Site *</Label>
          <Select 
            value={formData.site_id} 
            onValueChange={(value) => setFormData({ ...formData, site_id: value })}
            required
          >
            <SelectTrigger id="site" data-testid="event-site">
              <SelectValue placeholder="Sélectionnez un site" />
            </SelectTrigger>
            <SelectContent>
              {sites.map((site) => (
                <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isSiteAccount && (
          <div className="space-y-2">
            <Label>Agent rédacteur *</Label>
            <Popover open={redacteurPickerOpen} onOpenChange={setRedacteurPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  disabled={!formData.site_id}
                  className="w-full justify-between font-normal"
                  data-testid="redacteur-picker-trigger"
                >
                  {siteAgents.find(a => a.id === redacteurId)?.name || (formData.site_id ? 'Sélectionnez un agent' : 'Choisissez d\'abord un site')}
                  <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0">
                <Command>
                  <CommandInput placeholder="Rechercher un agent..." />
                  <CommandList>
                    <CommandEmpty>Aucun agent trouvé pour ce site.</CommandEmpty>
                    <CommandGroup>
                      {siteAgents.map(a => (
                        <CommandItem
                          key={a.id}
                          value={a.name}
                          onSelect={() => {
                            setRedacteurId(a.id);
                            setRedacteurPin('');
                            setPinVerified(false);
                            setPinError('');
                            setRedacteurPickerOpen(false);
                            // La popup de saisie du code personnel s'ouvre
                            // automatiquement juste après le choix de l'agent.
                            setRedacteurPinDialogOpen(true);
                          }}
                        >
                          {a.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <p className="text-xs text-gray-500 dark:text-slate-400">Permet de savoir qui a réellement rédigé cet événement.</p>

            {redacteurId && (
              <div className="pt-1">
                {pinVerified ? (
                  <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    <span>Code personnel confirmé</span>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-xs"
                      onClick={() => { setRedacteurPin(''); setPinVerified(false); setPinError(''); setRedacteurPinDialogOpen(true); }}
                      data-testid="redacteur-pin-edit"
                    >
                      Modifier
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => setRedacteurPinDialogOpen(true)}
                    data-testid="redacteur-pin-open-btn"
                  >
                    <KeyRound className="h-4 w-4" />
                    Saisir le code personnel de l'agent *
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Popup de saisie du code personnel du rédacteur : dès les 4 chiffres
            renseignés, le code est vérifié immédiatement auprès du serveur -
            elle ne se ferme que si le code est le bon. */}
        <Dialog open={redacteurPinDialogOpen} onOpenChange={(open) => { if (!pinChecking) setRedacteurPinDialogOpen(open); }}>
          <DialogContent className="max-w-xs">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <KeyRound className="h-4 w-4 text-indigo-600" />
                Code personnel
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              Demandé à {siteAgents.find(a => a.id === redacteurId)?.name || 'l\'agent sélectionné'} pour confirmer son identité.
            </p>
            <div className="flex flex-col items-center gap-2 py-2">
              <InputOTP
                maxLength={4}
                inputMode="numeric"
                value={redacteurPin}
                onChange={(value) => { setRedacteurPin(value); setPinError(''); }}
                onComplete={(value) => verifyRedacteurPin(value, redacteurId)}
                disabled={pinChecking}
                data-testid="redacteur-pin-otp"
                autoFocus
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} mask />
                  <InputOTPSlot index={1} mask />
                  <InputOTPSlot index={2} mask />
                  <InputOTPSlot index={3} mask />
                </InputOTPGroup>
              </InputOTP>

              {pinChecking && (
                <p className="text-xs text-slate-500 dark:text-slate-400">Vérification...</p>
              )}

              {pinError && (
                <div
                  className="flex items-start gap-2 p-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 text-xs w-full"
                  data-testid="redacteur-pin-error"
                >
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>{pinError}</span>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <div className="space-y-2">
          <Label htmlFor="category">Catégorie *</Label>
          <Popover
            open={categoryPickerOpen}
            onOpenChange={(open) => { setCategoryPickerOpen(open); if (!open) setCategorySearchQuery(''); }}
          >
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                className="w-full justify-between font-normal"
                data-testid="event-category"
              >
                {formData.category || 'Sélectionnez ou recherchez'}
                <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
              <Command>
                <CommandInput
                  placeholder="Rechercher une catégorie ou un type de mot-clé..."
                  value={categorySearchQuery}
                  onValueChange={setCategorySearchQuery}
                />
                <CommandList>
                  <CommandEmpty>Aucun résultat.</CommandEmpty>
                  <CommandGroup heading="Catégories">
                    {CATEGORIES.map((cat) => (
                      <CommandItem
                        key={cat.value}
                        value={cat.value}
                        onSelect={() => {
                          setFormData(prev => ({ ...prev, category: cat.value, event_type: '', extra_fields: {} }));
                          setCategoryPickerOpen(false);
                        }}
                        data-testid={`event-category-item-${cat.value}`}
                      >
                        {cat.value}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  {/* Le detail des types n'apparait que lorsqu'une recherche est saisie,
                      pour ne pas noyer la liste des categories par defaut. */}
                  {categorySearchQuery.trim() && (
                    <CommandGroup heading="Types d'événement">
                      {ALL_EVENT_TYPES.map((entry, idx) => (
                        <CommandItem
                          key={`${entry.category}-${entry.label}-${idx}`}
                          value={`${entry.label} ${entry.category}`}
                          onSelect={() => {
                            setFormData(prev => ({
                              ...prev,
                              category: entry.category,
                              event_type: entry.type,
                              // Repart d'un extra_fields propre : ce qui restait de la
                              // selection precedente (ancien sous-type, materiel...) ne
                              // doit pas trainer une fois qu'un nouveau type est choisi ici.
                              extra_fields: entry.subtypeField ? { [entry.subtypeField]: entry.subtype } : {}
                            }));
                            setCategoryPickerOpen(false);
                          }}
                          data-testid={`event-type-search-item-${entry.category}-${entry.label}`}
                        >
                          {entry.label} <span className="ml-2 text-xs text-slate-400">({entry.category})</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <p className="text-xs text-gray-500 dark:text-slate-400">
            Tapez un mot-clé (ex: "alarme") pour remplir directement la catégorie et le type ci-dessous.
          </p>
        </div>
      </div>

      {selectedCategory && (
        <div className="space-y-2">
          <Label htmlFor="event_type">Type d'événement *</Label>
          <Select 
            value={formData.event_type} 
            onValueChange={(value) => setFormData({ ...formData, event_type: value, extra_fields: {} })}
            required
          >
            <SelectTrigger id="event_type" data-testid="event-type">
              <SelectValue placeholder="Sélectionnez un type" />
            </SelectTrigger>
            <SelectContent>
              {selectedCategory.types.map((type) => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Sous-type pour une alarme (Sûreté ou Incendie), juste sous le type d'événement */}
      {isAlarm && (
        <div className="space-y-2">
          <Label htmlFor="alarm_subtype">Sous-type *</Label>
          <Select
            value={formData.extra_fields.alarm_subtype || ''}
            onValueChange={(value) => setFormData({
              ...formData,
              extra_fields: { ...formData.extra_fields, alarm_subtype: value }
            })}
            required
          >
            <SelectTrigger id="alarm_subtype" data-testid="alarm-subtype">
              <SelectValue placeholder="Sélectionnez un sous-type" />
            </SelectTrigger>
            <SelectContent>
              {getAlarmSubtypes(formData.category).map((subtype) => (
                <SelectItem key={subtype} value={subtype}>{subtype}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Sous-type pour Secours à personnes (Client/Employé), juste sous le type d'événement */}
      {isSecours && (
        <div className="space-y-2">
          <Label htmlFor="secours_subtype">Sous-type *</Label>
          <Select
            value={formData.extra_fields.secours_subtype || ''}
            onValueChange={(value) => setFormData({
              ...formData,
              extra_fields: { ...formData.extra_fields, secours_subtype: value }
            })}
            required
          >
            <SelectTrigger id="secours_subtype" data-testid="secours-subtype">
              <SelectValue placeholder="Sélectionnez un sous-type" />
            </SelectTrigger>
            <SelectContent>
              {SECOURS_SUBTYPES.map((subtype) => (
                <SelectItem key={subtype} value={subtype}>{subtype}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="event_datetime">Date et heure *</Label>
        <Input
          id="event_datetime"
          data-testid="event-datetime"
          type="datetime-local"
          value={formData.event_datetime}
          onChange={(e) => setFormData({ ...formData, event_datetime: e.target.value })}
          max={user?.role === 'admin' ? undefined : getParisDateTimeLocal(5)}
          required
        />
      </div>

      {/* Un agent peut créer l'événement librement même daté de plus de 15
          minutes dans le passé : il sera simplement en attente de validation
          par un chef de poste (information non bloquante). */}
      {isLateEntry && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-300 text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Cet événement est daté de plus de 15 minutes dans le passé : il sera créé immédiatement, mais restera en attente de validation par un chef de poste.</span>
        </div>
      )}

      {/* Perception/restitution du matériel, pour la prise et la fin de service */}
      {isServiceCheckin && (
        <div className="space-y-3 p-4 bg-blue-50 dark:bg-slate-800 border border-blue-100 dark:border-slate-700 rounded-lg">
          <Label>{isPriseService ? 'Perception du matériel' : 'Restitution du matériel'}</Label>
          <div className="space-y-3">
            {MATERIEL_ITEMS.map((item) => {
              const data = materiel[item.key] || {};
              return (
                <div key={item.key} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`materiel-${item.key}`}
                      checked={!!data.checked}
                      onCheckedChange={(checked) => updateMateriel(item.key, { checked: !!checked })}
                      data-testid={`materiel-${item.key}`}
                    />
                    <Label htmlFor={`materiel-${item.key}`} className="font-normal cursor-pointer">{item.label}</Label>
                  </div>
                  {(item.hasEtat || item.hasComment) && data.checked && (
                    <div className="pl-6 space-y-2">
                      {item.hasEtat && (
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant={data.etat === 'bon' ? 'default' : 'outline'}
                            onClick={() => updateMateriel(item.key, { etat: 'bon' })}
                            data-testid={`materiel-${item.key}-bon`}
                          >
                            Bon état
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant={data.etat === 'abime' ? 'default' : 'outline'}
                            onClick={() => updateMateriel(item.key, { etat: 'abime' })}
                            data-testid={`materiel-${item.key}-abime`}
                          >
                            Abîmé
                          </Button>
                        </div>
                      )}
                      {item.hasComment && (
                        <Input
                          placeholder="Commentaire (facultatif)"
                          value={data.commentaire || ''}
                          onChange={(e) => updateMateriel(item.key, { commentaire: e.target.value })}
                          data-testid={`materiel-${item.key}-commentaire`}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!isServiceCheckin && !isKeyDeparture && !(formData.event_type === 'Retour clé' && selectedReturnKeyNumbers.length > 0) && descriptionField}

      {/* Extra fields for Apprehension */}
      {isApprehension && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-blue-50 dark:bg-slate-800 border border-blue-100 dark:border-slate-700 rounded-lg">
          <div className="space-y-2">
            <Label htmlFor="apprehension_number">N° d'appréhension</Label>
            <Input
              id="apprehension_number"
              data-testid="apprehension-number"
              placeholder="Numéro"
              value={formData.extra_fields.apprehension_number || ''}
              onChange={(e) => setFormData({
                ...formData,
                extra_fields: { ...formData.extra_fields, apprehension_number: e.target.value }
              })}
            />
            <p className="text-[11px] text-gray-500 dark:text-slate-400">Suggéré automatiquement, modifiable si besoin.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="amount">Montant</Label>
            <Input
              id="amount"
              data-testid="apprehension-amount"
              type="number"
              placeholder="0.00 €"
              value={formData.extra_fields.amount || ''}
              onChange={(e) => setFormData({
                ...formData,
                extra_fields: { ...formData.extra_fields, amount: e.target.value }
              })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="article_count">Nombre d'articles</Label>
            <Input
              id="article_count"
              data-testid="apprehension-articles"
              type="number"
              placeholder="0"
              value={formData.extra_fields.article_count || ''}
              onChange={(e) => setFormData({
                ...formData,
                extra_fields: { ...formData.extra_fields, article_count: e.target.value }
              })}
            />
          </div>
        </div>
      )}

      {/* Retour de clé : liste à cocher des clés actuellement perçues et non
          rendues sur le site, pour générer automatiquement la description
          ("Restitution de la clé N° X par la société X (technicien)"). */}
      {formData.event_type === 'Retour clé' && pendingKeysForReturn.length > 0 && (
        <div className="p-4 bg-blue-50 dark:bg-slate-800 border border-blue-100 dark:border-slate-700 rounded-lg space-y-2">
          <Label>Clé(s) restituée(s) *</Label>
          <div className="space-y-1.5">
            {pendingKeysForReturn.map((k) => {
              const num = k.extra_fields?.badge_number;
              const checked = selectedReturnKeyNumbers.includes(num);
              return (
                <div key={k.id} className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
                  <Checkbox
                    id={`return-key-${k.id}`}
                    checked={checked}
                    onCheckedChange={(v) => toggleReturnKey(num, !!v)}
                    data-testid={`return-key-checkbox-${num}`}
                  />
                  <label htmlFor={`return-key-${k.id}`} className="text-sm cursor-pointer text-gray-700 dark:text-slate-200">
                    Clé n° {num} — {k.extra_fields?.company_name} ({k.extra_fields?.technician_name})
                  </label>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-gray-500 dark:text-slate-400">
            La description est générée automatiquement pour chaque clé cochée.
          </p>
        </div>
      )}

      {/* Extra fields for Badge/Key */}
      {isBadgeOrKey && !isKeyDeparture && !(formData.event_type === 'Retour clé' && pendingKeysForReturn.length > 0) && (
        <div className="p-4 bg-blue-50 dark:bg-slate-800 border border-blue-100 dark:border-slate-700 rounded-lg">
          <div className="space-y-2">
            <Label htmlFor="badge_number">Numéro{formData.event_type === 'Retour clé' ? ' *' : ''}</Label>
            <Input
              id="badge_number"
              data-testid="badge-number"
              placeholder="Numéro de badge/clé"
              value={formData.extra_fields.badge_number || ''}
              onChange={(e) => setFormData({
                ...formData,
                extra_fields: { ...formData.extra_fields, badge_number: e.target.value }
              })}
              required={formData.event_type === 'Retour clé'}
            />
          </div>
        </div>
      )}

      {/* Champs obligatoires pour un départ de clé : société, technicien,
          téléphone et numéro de clé, pour permettre le suivi des clés non
          rendues (bandeau d'alerte sur la main courante). */}
      {isKeyDeparture && (
        <div className="p-4 bg-blue-50 dark:bg-slate-800 border border-blue-100 dark:border-slate-700 rounded-lg space-y-3">
          <div className="space-y-2">
            <Label htmlFor="key_company_name">Nom de la société *</Label>
            <Input
              id="key_company_name"
              data-testid="key-company-name"
              value={formData.extra_fields.company_name || ''}
              onChange={(e) => setFormData({
                ...formData,
                extra_fields: { ...formData.extra_fields, company_name: e.target.value }
              })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="key_technician_name">Nom du technicien *</Label>
            <Input
              id="key_technician_name"
              data-testid="key-technician-name"
              value={formData.extra_fields.technician_name || ''}
              onChange={(e) => setFormData({
                ...formData,
                extra_fields: { ...formData.extra_fields, technician_name: e.target.value }
              })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="key_phone_number">N° de téléphone *</Label>
            <Input
              id="key_phone_number"
              data-testid="key-phone-number"
              type="tel"
              value={formData.extra_fields.phone_number || ''}
              onChange={(e) => setFormData({
                ...formData,
                extra_fields: { ...formData.extra_fields, phone_number: e.target.value }
              })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="key_badge_number">Numéro de clé *</Label>
            <Input
              id="key_badge_number"
              data-testid="key-badge-number"
              value={formData.extra_fields.badge_number || ''}
              onChange={(e) => setFormData({
                ...formData,
                extra_fields: { ...formData.extra_fields, badge_number: e.target.value }
              })}
              required
            />
          </div>
          <div className="pt-1 border-t border-blue-100 dark:border-slate-700">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                id="key_departure_final"
                data-testid="key-departure-final"
                checked={!!formData.extra_fields.departure_final}
                onCheckedChange={(checked) => setFormData({
                  ...formData,
                  extra_fields: {
                    ...formData.extra_fields,
                    departure_final: checked,
                    ...(checked ? { alert_time_1: '', alert_time_2: '' } : {})
                  }
                })}
              />
              Départ définitif (aucun retour attendu, pas de rappel)
            </label>
          </div>

          {!formData.extra_fields.departure_final && (
            <div className="grid grid-cols-2 gap-3 pt-1 border-t border-blue-100 dark:border-slate-700">
              <div className="space-y-2">
                <Label htmlFor="key_alert_time_1">1ᵉʳ rappel de non-restitution *</Label>
                <Input
                  id="key_alert_time_1"
                  type="time"
                  data-testid="key-alert-time-1"
                  value={formData.extra_fields.alert_time_1 || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    extra_fields: { ...formData.extra_fields, alert_time_1: e.target.value }
                  })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="key_alert_time_2">2ᵉ rappel de non-restitution *</Label>
                <Input
                  id="key_alert_time_2"
                  type="time"
                  data-testid="key-alert-time-2"
                  value={formData.extra_fields.alert_time_2 || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    extra_fields: { ...formData.extra_fields, alert_time_2: e.target.value }
                  })}
                  required
                />
              </div>
              <p className="col-span-2 text-xs text-gray-500 dark:text-slate-400">
                Ex: 17h puis 18h pour une journée classique, ou 05h puis 08h le lendemain pour une clé prise en fin de journée pour toute la nuit.
              </p>
            </div>
          )}
        </div>
      )}

      {isKeyDeparture && descriptionField}

      {/* File Upload */}
      <div className="space-y-2">
        <Label>Pièces jointes</Label>
        <FileUpload
          onFilesUploaded={(fileIds) => setFormData({ ...formData, attachments: fileIds })}
          existingFiles={[]}
        />
      </div>

      {/* Signature Canvas */}
      {needsSignature && (
        <div className="space-y-2">
          <Label>Signature *</Label>
          <div className="relative">
            <canvas
              ref={canvasRef}
              data-testid="signature-canvas"
              width={500}
              height={200}
              className="signature-canvas w-full"
              style={{ touchAction: 'none' }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute top-2 right-2"
              onClick={clearSignature}
              data-testid="clear-signature"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {canMarkImportant && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900">
          <Checkbox
            id="is_important"
            checked={formData.is_important}
            onCheckedChange={(checked) => setFormData({ ...formData, is_important: !!checked })}
            data-testid="is-important-checkbox"
          />
          <Label htmlFor="is_important" className="text-sm font-normal text-red-700 dark:text-red-300 cursor-pointer flex items-start gap-1">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Marquer comme événement important
              <span className="block text-xs text-red-500 dark:text-red-400">
                Envoie un email automatique aux clients de ce site avec la description et le type de l'événement
              </span>
            </span>
          </Label>
        </div>
      )}

      <div className="flex gap-3 pt-4">
        <Button type="submit" className="btn-primary flex-1" disabled={loading} data-testid="submit-event">
          {loading ? 'Création...' : 'Créer l\'événement'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>Annuler</Button>
      </div>
    </form>

    {/* Popup affichée uniquement à la création, si aucun matériel n'est coché :
        laisse le choix de valider quand même ou de revenir modifier la saisie. */}
    <Dialog open={showNoMaterielConfirm} onOpenChange={setShowNoMaterielConfirm}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Aucun matériel {isPriseService ? 'perçu' : 'rendu'}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600 dark:text-slate-300">
          Vous n'avez aucun matériel à {isPriseService ? 'percevoir' : 'rendre'} ? Vous pouvez valider l'événement tel quel, ou revenir en arrière pour compléter la saisie avant publication.
        </p>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setShowNoMaterielConfirm(false)} data-testid="no-materiel-modify">
            Modifier
          </Button>
          <Button
            type="button"
            className="btn-primary"
            onClick={() => { setShowNoMaterielConfirm(false); submitEvent(); }}
            data-testid="no-materiel-confirm"
          >
            Valider quand même
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default EventForm;
